import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { nip57 } from 'nostr-tools';
import type { Fundraiser, FundraiserProgress, Supporter } from '@/types/fundraiser';

export function useFundraiserProgress(fundraiser: Fundraiser | null | undefined) {
  const { nostr } = useNostr();

  return useQuery<FundraiserProgress & { supporters: Supporter[] }, Error>({
    queryKey: ['fundraiser-progress', fundraiser?.id],
    staleTime: 10000,
    refetchInterval: 15000,
    queryFn: async (c) => {
      if (!fundraiser) {
        return {
          totalRaised: 0,
          zapCount: 0,
          percentComplete: 0,
          isComplete: false,
          isClosed: false,
          supporters: [],
        };
      }

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      // Query zap receipts for this goal (NIP-75 uses 'e' tag with event ID)
      const zapEvents = await nostr.query([{
        kinds: [9735],
        '#e': [fundraiser.id],
      }], { signal });

      let totalRaised = 0;
      const supporterMap = new Map<string, { totalSats: number; zapCount: number; lastZappedAt: number }>();

      for (const zap of zapEvents) {
        if (fundraiser.closedAt && zap.created_at > fundraiser.closedAt) {
          continue;
        }

        let sats = 0;

        const bolt11Tag = zap.tags.find(([name]) => name === 'bolt11')?.[1];
        if (bolt11Tag) {
          try {
            sats = nip57.getSatoshisAmountFromBolt11(bolt11Tag);
          } catch (error) {
            console.warn('Failed to parse bolt11 amount:', error);
          }
        }

        if (sats === 0) {
          const amountTag = zap.tags.find(([name]) => name === 'amount')?.[1];
          if (amountTag) {
            const millisats = parseInt(amountTag);
            sats = Math.floor(millisats / 1000);
          }
        }

        if (sats === 0) {
          const descriptionTag = zap.tags.find(([name]) => name === 'description')?.[1];
          if (descriptionTag) {
            try {
              const zapRequest = JSON.parse(descriptionTag);
              const requestAmountTag = zapRequest.tags?.find(([name]: string[]) => name === 'amount')?.[1];
              if (requestAmountTag) {
                const millisats = parseInt(requestAmountTag);
                sats = Math.floor(millisats / 1000);
              }
            } catch (error) {
              console.warn('Failed to parse description JSON:', error);
            }
          }
        }

        if (sats > 0) {
          totalRaised += sats;

          const senderPubkey = zap.tags.find(([name]) => name === 'P')?.[1];
          if (senderPubkey) {
            const existing = supporterMap.get(senderPubkey) || {
              totalSats: 0,
              zapCount: 0,
              lastZappedAt: 0,
            };

            supporterMap.set(senderPubkey, {
              totalSats: existing.totalSats + sats,
              zapCount: existing.zapCount + 1,
              lastZappedAt: Math.max(existing.lastZappedAt, zap.created_at),
            });
          }
        }
      }

      const supporters: Supporter[] = Array.from(supporterMap.entries())
        .map(([pubkey, data]) => ({
          pubkey,
          ...data,
        }))
        .sort((a, b) => b.totalSats - a.totalSats);

      // Convert goal from millisats to sats for comparison
      const goalInSats = Math.floor(fundraiser.goal / 1000);
      const percentComplete = Math.min((totalRaised / goalInSats) * 100, 100);
      const isComplete = totalRaised >= goalInSats;
      const isClosed = fundraiser.closedAt ? Date.now() / 1000 > fundraiser.closedAt : false;

      return {
        totalRaised,
        zapCount: zapEvents.length,
        percentComplete,
        isComplete,
        isClosed,
        supporters,
      };
    },
    enabled: !!fundraiser,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}
